import { html, LitElement, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import templateRenderer from "@slhuicore/utils/template-renderer.js";
import * as Calendar from "./calendar.constants";

import { style } from "./assets/calendar.generated-style";
import { CalendarDay, ItemData } from "./calendar.type";

/**
 * SLH component to create a calendar and add items to it.
 *
 * @CustomElement slh-calendar
 *
 * @cssprop --slh-default-font-family - Calendar font family.
 * @cssprop --slh-base-bold-font-weight - Calendar font weight. Month font weight.
 * @cssprop --slh-base-semi-bold-font-weight - Days font weight. Calendar week days font weight.
 * @cssprop --slh-base-xxs-font-size - Calendar week days font size.
 * @cssprop --slh-base-xs-font-size - Calendar days font size.
 * @cssprop --slh-base-m-font-size - Calendar month font size.
 * @cssprop --slh-color-neutral-shade-00 - Calendar background color. Popup content background color.
 * @cssprop --slh-color-neutral-shade-10 - Scrollbar track background.
 * @cssprop --slh-color-neutral-shade-20 - Day hover background color. Today background color. Item bottom border color.
 * @cssprop --slh-color-neutral-shade-40 - Other month days font color. Popup icons color.
 * @cssprop --slh-color-neutral-shade-60 - Calendar arrows hover color. Week days color.
 * @cssprop --slh-color-neutral-shade-80 - Calendar month color.
 * @cssprop --slh-color-neutral - Calendar arrows color.
 * @cssprop --slh-color-primary - Item day dot background color.
 
 * ** slots
 * @slot item - Slot for custom rendering of day popup
 * @slot item-style - Slot for rendering custom CSS which applies to day popup content
 */
@customElement("slh-calendar")
export default class SlhCalendar extends Translatable(LitElement) {
  /**
   * First day of week
   */
  @property({
    type: Boolean,
    attribute: "sunday-first",
    reflect: true,
  })
  sundayFirst: boolean = false;

  /**
   * Define calendar items
   */
  @property({
    type: Array,
    attribute: true,
    hasChanged(newVal: ItemData[], oldVal: ItemData[]) {
      return JSON.stringify(newVal) !== JSON.stringify(oldVal);
    },
  })
  private items: ItemData[] = [];

  private itemsTemplate: string = "";

  private itemsTemplateStyle: string = "";

  private month: number;

  private year: number;

  private localDate: Date = new Date();

  /*
   * Current date in UTC
   */
  private todayUTCDate: Date = new Date(
    Date.UTC(
      this.localDate.getUTCFullYear(),
      this.localDate.getUTCMonth(),
      this.localDate.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  /*
   * Selected date in UTC
   */
  private dateUTC: Date = new Date(
    Date.UTC(
      this.localDate.getUTCFullYear(),
      this.localDate.getUTCMonth(),
      this.localDate.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  static styles = style;

  constructor() {
    super();
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
  }

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("calendar-wrapper");
  }

  render(): TemplateResult | void {
    return html`
      <div class="calendar-container">
        <div class="date-holder">
          ${this.renderPrevMonth()}
          <div class="date">${this.getMonthName(this.month)} ${this.year}</div>
          ${this.renderNextMonth()}
        </div>
        <div id="calendar">${this.renderCalendar()}</div>
      </div>
      <slot
        name="item-style"
        @slotchange="${this.onTemplateStyleSlotChange}"
      ></slot>
      <slot name="item" @slotchange="${this.onTemplateSlotChange}"></slot>
    `;
  }

  private onTemplateSlotChange({
    target,
  }: Event & { target: HTMLSlotElement }): void {
    const elements = target.assignedElements() as HTMLElement[];
    elements.forEach((element: HTMLElement) => {
      if (element.tagName === "TEMPLATE") {
        const contents = element.innerHTML.trim();
        if (contents) {
          this.itemsTemplate = contents;
        }
      }
    });
    this.requestUpdate();
  }

  private onTemplateStyleSlotChange({
    target,
  }: Event & { target: HTMLSlotElement }): void {
    const elements = target.assignedElements() as HTMLElement[];
    elements.forEach((element: HTMLElement) => {
      if (element.tagName === "TEMPLATE") {
        const contents = element.innerHTML.trim();
        if (contents) {
          this.itemsTemplateStyle = contents;
        }
      }
    });
    this.requestUpdate();
  }

  /**
   * @returns {TemplateResult}
   */
  protected renderPrevMonth(): TemplateResult {
    return html`<div @click="${this.prevMonth}" class="prev-month">
      <slh-icon type="${IconType.ICO_ARROW_OPEN_LEFT}"></slh-icon>
      </dev>`;
  }

  /**
   * @returns {TemplateResult}
   */
  protected renderNextMonth(): TemplateResult {
    return html`<div @click="${this.nextMonth}" class="prev-month">
      <slh-icon type="${IconType.ICO_ARROW_OPEN_RIGHT}"></slh-icon>
      </dev>`;
  }

  protected getMonthName(month: number): string {
    const monthsLabels = [
      this.trans("calendar.months.jan"),
      this.trans("calendar.months.feb"),
      this.trans("calendar.months.mar"),
      this.trans("calendar.months.apr"),
      this.trans("calendar.months.may"),
      this.trans("calendar.months.jun"),
      this.trans("calendar.months.jul"),
      this.trans("calendar.months.aug"),
      this.trans("calendar.months.sep"),
      this.trans("calendar.months.oct"),
      this.trans("calendar.months.nov"),
      this.trans("calendar.months.dec"),
    ];
    return monthsLabels[month];
  }

  protected getDaysLabels(): Array<string> {
    const sunday = [this.trans("calendar.weekdays-short.su")];
    const days = [
      this.trans("calendar.weekdays-short.mo"),
      this.trans("calendar.weekdays-short.tu"),
      this.trans("calendar.weekdays-short.we"),
      this.trans("calendar.weekdays-short.th"),
      this.trans("calendar.weekdays-short.fr"),
      this.trans("calendar.weekdays-short.sa"),
    ];
    if (this.sundayFirst) {
      return sunday.concat(days);
    }
    return days.concat(sunday);
  }

  protected getDayLabel(day: number): string {
    return this.getDaysLabels()[day];
  }

  /**
   * Generate calendar table
   * @returns TemplateResult
   */
  protected renderCalendar(): TemplateResult {
    // calendar content
    return html`<table class="calendar-table">
      ${this.renderWeekDays()} ${this.renderDays()}
    </table>`;
  }

  protected renderDays(): TemplateResult {
    const calendarDays: Array<CalendarDay> = this.getCalendarDays();
    const weeksplit: Array<Array<CalendarDay>> =
      this.getDaysByWeek(calendarDays);

    return html`${weeksplit.map(
      (week: Array<CalendarDay>) =>
        html`<tr class="week">
          ${week.map(
            (object: CalendarDay) =>
              html`<td
                class="day ${object.class}"
              >
                ${this.renderPopup(object.item)}
                <span class="${object.class}">
                  ${object.date.getDate()}
                  <div class='popup-arrow-down'></div><div class='item-dot'></div></span>
                </span>                
              </td>`
          )}
        </tr>`
    )}`;
  }

  protected renderPopup(items: ItemData[]): TemplateResult {
    if (items.length > 0) {
      return html`<div class="popup">
        <div class="popup-content-holder">${this.renderItemPopups(items)}</div>
      </div>`;
    }
    return html``;
  }

  protected renderItemPopups(items: ItemData[]): HTMLElement {
    const popupContent: HTMLElement = document.createElement("div");
    popupContent.classList.add("popup-content");
    popupContent.innerHTML = this.itemsTemplateStyle;
    items.forEach((item: ItemData) => {
      const popupContentItem: HTMLElement = document.createElement("div");
      popupContentItem.classList.add("popup-content-item");
      popupContentItem.innerHTML = templateRenderer.render(
        this.itemsTemplate,
        item
      );
      popupContent.appendChild(popupContentItem);
    });
    return popupContent;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDaysByWeek(
    calendarDays: Array<CalendarDay>
  ): Array<Array<CalendarDay>> {
    const weeksplit: Array<Array<CalendarDay>> = [];
    let days: Array<CalendarDay> = [];

    calendarDays.forEach((object: CalendarDay, key: number) => {
      days.push(object);
      if (key % 7 === 6 && days.length > 0) {
        weeksplit.push(days);
        days = [];
      }
    });

    return weeksplit;
  }

  // eslint-disable-next-line class-methods-use-this
  protected renderWeekDays(): TemplateResult {
    return html`<tr class="week-days">
      ${Array.from(Array(Calendar.WEEK_DAYS_LENGHT).keys()).map(
        (i: number) => html`<td class="day">${this.getDayLabel(i)}</td>`
      )}
    </tr>`;
  }

  protected getCalendarDays(): Array<CalendarDay> {
    const days: Array<CalendarDay> = [];

    const month: number = this.dateUTC.getMonth();
    const year: number = this.dateUTC.getFullYear();
    const daysInMonth = this.getDaysInMonth(month, year);
    const firstDayDate = new Date(Date.UTC(year, month, 1));
    const jan = 0;
    const dec = 11;
    const prevMonth = month === jan ? dec : month - 1;
    const prevYear = prevMonth === dec ? year - 1 : year;
    const prevDays = this.getDaysInMonth(prevMonth, prevYear);

    let nextDaysDate = 1;
    let currentDate = 1;
    let prevMonthIncrementer = 0;
    let prevMonthDayIncrementer = 1;
    let firstDayWeekday: number = firstDayDate.getDay();

    // dates loop
    if (!this.sundayFirst && firstDayWeekday === 0) {
      firstDayWeekday = 7;
    }
    if (!this.sundayFirst) {
      prevMonthIncrementer = 1;
      prevMonthDayIncrementer = 2;
    }

    for (let i = 0; i < Calendar.CALENDAR_DAYS_LENGHT; i += 1) {
      if (i + prevMonthIncrementer < firstDayWeekday) {
        // previous month's day
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth() - 1,
            prevDays - firstDayWeekday + i + prevMonthDayIncrementer
          )
        );
        const calendarDay: CalendarDay = {
          class: "other-month",
          date: dayDate,
          item: [],
        };
        days.push(calendarDay);
      } else if (currentDate > daysInMonth) {
        // next month's day
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth() + 1,
            nextDaysDate
          )
        );
        const calendarDay: CalendarDay = {
          class: "other-month",
          date: dayDate,
          item: [],
        };
        days.push(calendarDay);
        nextDaysDate += 1;
      } else {
        // current month
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth(),
            currentDate
          )
        );
        const activeItem: ItemData[] = this.getActiveItem(dayDate);

        const calendarDay: CalendarDay = {
          class: this.dayClasses(dayDate, activeItem),
          date: dayDate,
          item: activeItem,
        };
        days.push(calendarDay);
        currentDate += 1;
      }
    }

    return days;
  }

  protected getActiveItem(date: Date): ItemData[] {
    const cleardDate: Date = new Date(date);
    cleardDate.setHours(0, 0, 0, 0);
    return this.items?.filter((item: ItemData) => {
      const fromTimestamp = Date.parse(item.dateTimeFrom);
      if (Number.isNaN(fromTimestamp)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[slh-calendar] Start datetime of calendar item is not properly formatted: "${item.dateTimeFrom}"`
        );
        return false;
      }
      const toTimestamp = Date.parse(item.dateTimeTo);
      if (Number.isNaN(toTimestamp)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[slh-calendar] End datetime of calendar item is not properly formatted: "${item.dateTimeTo}"`
        );
        return false;
      }

      const dateTimeFrom = new Date(fromTimestamp);
      const dateTimeTo = new Date(toTimestamp);
      dateTimeFrom.setHours(0, 0, 0, 0);
      dateTimeTo.setHours(0, 0, 0, 0);

      return (
        cleardDate.getTime() >= dateTimeFrom.getTime() &&
        cleardDate.getTime() <= dateTimeTo.getTime()
      );
    });
  }

  protected dayClasses(date: Date, activeItem: ItemData[]): string {
    const result: Array<string> = [];
    if (this.todayUTCDate.getTime() === date.getTime()) {
      result.push("today");
    }

    if (activeItem.length > 0) {
      result.push("active");
    }
    return result.join(" ");
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDaysInMonth(month: number, year: number): number {
    // 0 = last day of the previous month
    return new Date(year, month + 1, 0).getDate();
  }

  protected nextMonth(): void {
    if (this.dateUTC.getMonth() === 11) {
      this.dateUTC = new Date(this.dateUTC.getFullYear() + 1, 0, 1);
    } else {
      this.dateUTC = new Date(
        this.dateUTC.getFullYear(),
        this.dateUTC.getMonth() + 1,
        1
      );
    }
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
    this.requestUpdate();
  }

  protected prevMonth(): void {
    if (this.dateUTC.getMonth() === 0) {
      this.dateUTC = new Date(this.dateUTC.getFullYear() - 1, 11, 1);
    } else {
      this.dateUTC = new Date(
        this.dateUTC.getFullYear(),
        this.dateUTC.getMonth() - 1,
        1
      );
    }
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
    this.requestUpdate();
  }
}
